//
//  FitbitIntegration.tsx
//
//  isana.net,inc. Proprietary and Confidential
//

import { type ReactElement, useState, useCallback } from "react";
import Button from "@mui/material/Button";
import {
  Alert,
  Box,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
} from "@mui/material";
import {
  unlinkUserByFitbitId,
  unlinkUserByFitinsightUniqueId,
} from "./network/api";
import { CONTACT_URL, FITINSIGHT_UNIQUE_ID_USED } from "./utils/env";
import { PageTitle } from "./component/PageTitle";
import BackToAdminButton from "./component/BackToAminMainButton";
import TitleBar from "./component/TitleBar";

const FitbitIntegration = (): ReactElement => {
  const [id, setId] = useState("");
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<"success" | "error">("success");

  const onClickDisconnect = useCallback(
    async (event: any) => {
      event.preventDefault();
      // ID未入力なら確認ダイアログを開かず先にエラー表示（空IDのダイアログを出さない）
      if (id === "") {
        showErrorMessage("ユーザのIDを入力してください");
        return;
      }
      setShowConfirm(true);
    },
    [id]
  );

  const onClickYesButton = useCallback(
    async (event: any) => {
      event.preventDefault();
      setShowConfirm(false);
      if (id === "") {
        showErrorMessage("ユーザのIDを入力してください");
        return;
      }
      const success = FITINSIGHT_UNIQUE_ID_USED
        ? await unlinkUserByFitinsightUniqueId(id)
        : await unlinkUserByFitbitId(id);
      if (success) {
        showSuccessMessage("FITINSIGHTとの連携解除に成功しました");
      } else {
        showErrorMessage("FITINSIGHTとの連携解除に失敗しました");
      }
    },
    [id]
  );

  const showErrorMessage = useCallback((msg: string) => {
    setMessage(msg);
    setSeverity("error");
    setError(true);
    setTimeout(() => {
      setError(false);
    }, 3000);
  }, []);

  const showSuccessMessage = useCallback((msg: string) => {
    setMessage(msg);
    setSeverity("success");
    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
    }, 3000);
  }, []);


  return (
    <>
      <TitleBar contactUrl={CONTACT_URL} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          width: "100%",
          padding: "10px 0",
        }}
      >
        <BackToAdminButton />
      </Box>
      <Container
        sx={{
          my: 5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <PageTitle title="ユーザ連携解除" />
        <Typography sx={{ mb: 4 }}>
          FITINSIGHTとの連携を解除したいユーザのIDを入力してください
        </Typography>
        <Box
          component="form"
          onSubmit={onClickDisconnect}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          <TextField
            type="text"
            name="Id"
            id="Id"
            onChange={(e) => setId(e.target.value)}
            placeholder="ID"
            sx={{ width: 300 }}
            label="ID"
            value={id}
          />

          <Button
            variant="contained"
            type="submit"
            sx={{ width: "fit-content", px: 4 }}
          >
            連携解除
          </Button>
          {(error || success) && (
            <Box sx={{ mt: 2, width: "100%", maxWidth: 400 }}>
              <Alert
                severity={severity}
                onClose={() => {
                  setError(false);
                  setSuccess(false);
                }}
              >
                {message}
              </Alert>
            </Box>
          )}
        </Box>
      </Container>
      <Dialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        aria-describedby="alert-dialog-description"
      >
        <DialogContent id="alert-dialog-description">
          ID「{id}」の連携を解除しますか？
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "center",
          }}>
          <Button onClick={() => setShowConfirm(false)}>いいえ</Button>
          <Button onClick={onClickYesButton}>はい</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FitbitIntegration;